<template>
  <div class="flex flex-col w-full h-full overflow-y-auto bg-surface-50 px-3.5 pt-3.5 pb-6">
    <!-- Storefront hero — the business page is the centerpiece -->
    <div
      class="rounded-[20px] p-4 pb-3.5 cursor-pointer relative overflow-hidden text-white"
      style="background: linear-gradient(135deg, #2c1461 0%, #49238f 55%, #6831d0 100%)"
      @click="$emit('open', 0)"
    >
      <div class="flex items-center gap-3">
        <img
          v-if="logoImage"
          :src="logoImage"
          alt="לוגו העסק"
          class="w-[52px] h-[52px] rounded-[14px] object-cover flex-none bg-white shadow-[0_4px_12px_rgba(0,0,0,.25)]"
        />
        <div
          v-else
          class="w-[52px] h-[52px] rounded-[14px] bg-white text-[#6831d0] flex items-center justify-center text-lg font-medium flex-none shadow-[0_4px_12px_rgba(0,0,0,.25)]"
        >{{ businessInitials }}</div>
        <div class="flex-1 min-w-0">
          <div class="text-[15px] font-medium truncate">{{ businessTitle }}</div>
          <div class="text-[11px] opacity-75 mt-0.5 truncate" style="direction: ltr; text-align: right">{{ siteUrlDisplay }}</div>
        </div>
        <div
          v-if="isSiteLocked"
          class="flex items-center gap-1.5 text-[10.5px] font-medium px-2.5 py-1 rounded-full flex-none"
          style="background: rgba(251,191,36,.18); color: #fcd34d"
        >
          <i class="pi pi-lock text-[9px]" /> נעול בסיסמה
        </div>
        <div
          v-else
          class="flex items-center gap-1.5 text-[10.5px] font-medium px-2.5 py-1 rounded-full flex-none"
          style="background: rgba(52,211,153,.18); color: #6ee7b7"
        >
          <span class="w-1.5 h-1.5 rounded-full bg-emerald-400" /> עמוד פעיל
        </div>
      </div>
      <div class="flex items-center gap-2 mt-3.5">
        <button
          type="button"
          class="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-white/[.14] text-xs font-medium"
          @click.stop="$emit('open', 0)"
        >
          <i class="pi pi-pencil text-[11px]" /> עריכת העמוד
        </button>
        <button
          type="button"
          class="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-white/[.14] text-xs font-medium"
          @click.stop="shareSite"
        >
          <i class="pi pi-share-alt text-[11px]" /> שיתוף
        </button>
      </div>
    </div>

    <!-- Live-value tiles — each shows its current configuration, not just a label -->
    <div class="grid grid-cols-2 gap-2.5 mt-3">
      <div
        class="bg-white border border-surface-200 rounded-[18px] px-3 pt-3 pb-2.5 cursor-pointer"
        @click="$emit('open', 1)"
      >
        <div class="flex items-center gap-1.5 mb-3">
          <div class="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center flex-none">
            <i class="pi pi-calendar !text-xs text-blue-700" />
          </div>
          <span class="text-xs font-medium flex-1">זימון תורים</span>
          <i class="pi pi-chevron-left !text-xs text-surface-200" />
        </div>
        <div class="text-[19px] font-medium tracking-tight">כל {{ stepGap }} דק׳</div>
        <div class="text-[10.5px] text-surface-500 mt-0.5">עד {{ maxDays }} ימים קדימה · {{ approveLabel }}{{ bufferLabel }}</div>
      </div>

      <div
        class="bg-white border border-surface-200 rounded-[18px] px-3 pt-3 pb-2.5 cursor-pointer"
        @click="$emit('open', 2)"
      >
        <div class="flex items-center gap-1.5 mb-3">
          <div class="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center flex-none">
            <i class="pi pi-credit-card !text-xs text-amber-700" />
          </div>
          <span class="text-xs font-medium flex-1">תשלומים</span>
          <i class="pi pi-chevron-left !text-xs text-surface-200" />
        </div>
        <div class="text-[19px] font-medium tracking-tight truncate">{{ paymentHeadline }}</div>
        <div class="text-[10.5px] text-surface-500 mt-0.5 truncate">{{ paymentHint }}</div>
      </div>
    </div>

    <!-- Account -->
    <div
      class="bg-white border border-surface-200 rounded-[18px] px-3.5 py-3 mt-2.5 flex items-center gap-3 cursor-pointer"
      @click="$emit('open', 3)"
    >
      <img
        v-if="profileImage"
        :src="profileImage"
        alt="תמונת פרופיל"
        class="w-10 h-10 rounded-full object-cover flex-none"
      />
      <div
        v-else
        class="w-10 h-10 rounded-full bg-primary-50 text-primary flex items-center justify-center text-sm font-medium flex-none"
      >{{ userInitials }}</div>
      <div class="flex-1 min-w-0">
        <div class="text-sm font-medium truncate">{{ fullName }}</div>
        <div class="text-[11px] text-surface-500 mt-0.5">אימייל, סיסמה והגדרות אישיות</div>
      </div>
      <i class="pi pi-chevron-left !text-xs text-surface-300 flex-none" />
    </div>

    <div class="text-center text-[10.5px] text-surface-400 mt-6">TimeIt · גרסה {{ appVersion }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useAdminStore } from '@app/stores';
import { useAdminSettingsStore } from '@app/stores/admin-settings';
import { useDrawer } from '@torim-app/shared-components';
import ShareSocials from '@app/components/admin/modals/ShareSocials.vue';
import { trackEvent } from '@app/composables/posthog';

defineEmits<{ open: [tab: number] }>();

const adminStore = useAdminStore();
const settingsStore = useAdminSettingsStore();
const { openDrawer } = useDrawer();

const appVersion = __APP_VERSION__;

const siteData = computed(() => adminStore.getBusinessSiteData);
const configurations = computed(() => siteData.value?.configurations);

const businessTitle = computed(() => adminStore.getBusinessTitle || siteData.value?.title || '');
const businessInitials = computed(() =>
  businessTitle.value.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('')
);
const logoImage = computed(() => siteData.value?.design?.logoImage);
const isSiteLocked = computed(() => configurations.value?.siteMode === 'locked');
const siteUrlDisplay = computed(() =>
  (settingsStore.getSettings?.general?.siteUrl ||
    `${import.meta.env.VITE_BUSINESS_WEBSITE_DOMAIN}/business/${siteData.value?.bid}`)
    .replace(/^https?:\/\//, '')
);

// Booking tile
const stepGap = computed(() => configurations.value?.appointmentsStepGap || 15);
const maxDays = computed(() => configurations.value?.allowToSchedule || 14);
const approveLabel = computed(() => configurations.value?.appointmentManualApprove ? 'אישור ידני' : 'אישור אוטומטי');
const bufferLabel = computed(() => {
  const buffer = configurations.value?.appointmentsBufferMinutes || 0;
  return buffer ? ` · רווח ${buffer} דק׳` : '';
});

// Payments tile
const paymentMode = computed(() => {
  const configs = configurations.value;
  if (configs?.schedulePaymentMode !== null && configs?.schedulePaymentMode !== undefined) return configs.schedulePaymentMode;
  return configs?.isScheduleRequirePayment ? 'force' : 'none';
});
const paymentHeadline = computed(() => {
  if (paymentMode.value === 'optional') return 'תשלום אופציונלי';
  if (paymentMode.value === 'force') {
    const pct = configurations.value?.scheduleAdvancedPaymentPercentage ?? 100;
    return pct === 100 ? 'תשלום מלא' : `מקדמה ${pct}%`;
  }
  return 'ללא תשלום';
});
const paymentHint = computed(() => {
  const parts: string[] = [];
  if (paymentMode.value === 'force') {
    const chargeLabels: Record<string, string> = {
      immediate: 'חיוב מיידי',
      'before-appointment': 'חיוב לפני התור',
      'save-card': 'שמירת כרטיס'
    };
    parts.push(chargeLabels[configurations.value?.scheduleChargeMode ?? 'immediate'] ?? 'חיוב מיידי');
  }
  parts.push(siteData.value?.hasPayments ? 'סליקה פעילה' : 'סליקה לא מחוברת');
  return parts.join(' · ');
});

// Account row
const fullName = computed(() => settingsStore.getSettings?.general?.fullName || '');
const profileImage = computed(() => settingsStore.getSettings?.general?.profileImage);
const userInitials = computed(() =>
  fullName.value.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('')
);

const shareSite = () => {
  trackEvent('settings_hub_share_clicked');
  openDrawer(ShareSocials, {}, { title: 'שיתוף אתר הנחיתה' });
};
</script>
